import { RangeKey, TimeRange } from '../types/superadmin';

/**
 * A TimeRange carried in the URL query string.
 *
 * The client drill-down holds its range in component state, but session review
 * lives on its own routes — so the selected period has to travel with the link
 * or a super admin lands on "last 30 days" after picking a custom window, and
 * the sessions they see stop matching the row they clicked. Putting it in the
 * URL also makes those pages linkable and back/forward-safe.
 *
 * The shape matches what the API already expects (`?range=`/`&from=`/`&to=`),
 * so there is one serialization to reason about rather than two.
 */

const VALID_KEYS: RangeKey[] = ['last7', 'last30', 'custom'];
const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const DEFAULT_RANGE_KEY: RangeKey = 'last30';

/** Query-string fragment for a range, without the leading '?' or '&'. */
export function rangeToQuery(range: TimeRange): string {
  if (range.key === 'custom' && range.from && range.to) {
    return `range=custom&from=${range.from}&to=${range.to}`;
  }
  return `range=${range.key}`;
}

/**
 * Read a range back out of the URL. Anything malformed falls back to the
 * default window rather than throwing — a hand-edited URL should degrade, not
 * break the page.
 */
export function rangeFromQuery(params: URLSearchParams): TimeRange {
  const key = params.get('range') as RangeKey | null;
  if (!key || !VALID_KEYS.includes(key)) return { key: DEFAULT_RANGE_KEY };

  if (key === 'custom') {
    const from = params.get('from');
    const to = params.get('to');
    if (from && to && DAY_PATTERN.test(from) && DAY_PATTERN.test(to)) {
      return { key: 'custom', from, to };
    }
    return { key: DEFAULT_RANGE_KEY };
  }
  return { key };
}

/** Link to one rep's sessions inside the given period. */
export const repSessionsPath = (tenantId: string, userId: string, range: TimeRange): string =>
  `/clients/${encodeURIComponent(tenantId)}/reps/${encodeURIComponent(userId)}/sessions?${rangeToQuery(range)}`;

/** Link back to a client drill-down, keeping the period. */
export const clientPath = (tenantId: string, range: TimeRange): string =>
  `/clients/${encodeURIComponent(tenantId)}?${rangeToQuery(range)}`;

/** Link to one session's review page, keeping the period for the back link. */
export const sessionPath = (tenantId: string, sessionId: string, range: TimeRange): string =>
  `/clients/${encodeURIComponent(tenantId)}/sessions/${encodeURIComponent(sessionId)}?${rangeToQuery(range)}`;
